import React, { FC, useState } from "react";
import Checkbox from "shared/Checkbox/Checkbox";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-hot-toast";
import ButtonTertiary from "shared/Button/ButtonTertiary";
import ExploreSidebarCard from "@/shared/ExploreSidebarCard/ExploreSidebarCard";
import { ShoppingBag } from "lucide-react";

interface FilterOption {
    id: string;
    label: string;
    value: string | undefined;
}

interface KitsDesktopFilterSidebarProps {
    selectedTypes: string[];
    selectedBudgets: string[];
    onTypeChange: (types: string[]) => void;
    onBudgetChange: (budgets: string[]) => void;
    showClear?: boolean;
    onClear?: () => void;
    filters: {
        types: FilterOption[];
        budgets: FilterOption[];
    };
}

const KitsDesktopFilterSidebar: FC<KitsDesktopFilterSidebarProps> = ({
    selectedTypes,
    selectedBudgets,
    onTypeChange,
    onBudgetChange,
    showClear,
    onClear,
    filters,
}) => {
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [priceError, setPriceError] = useState("");
    const [expandedSections, setExpandedSections] = useState({
        categories: true,
        budget: true,
    });

    const toggleSection = (section: "categories" | "budget") => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const handleTypeToggle = (value: string | undefined, checked: boolean) => {
        if (!value) return;
        // Single Select Logic
        if (checked) {
            onTypeChange([value]);
        } else {
            // Optional: Allow deselecting to show all? Or enforce one must be selected?
            // Usually Deselecting -> Empty -> "All"
            onTypeChange([]);
        }
    };

    const handleBudgetToggle = (value: string | undefined, checked: boolean) => {
        if (!value) return;

        if (checked) {
            onBudgetChange([value]);
        } else {
            onBudgetChange([]);
        }
    };

    const handlePriceInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        setter: React.Dispatch<React.SetStateAction<string>>
    ) => {
        const value = e.target.value;

        if (value.length > 5) {
            toast.error("Price cannot exceed 5 digits");
            return;
        }

        setter(value);
        // Clear any previous error if valid input
        if (priceError) setPriceError("");
    };


    const handleCustomPriceSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!minPrice || !maxPrice) return;

        const min = Number(minPrice);
        const max = Number(maxPrice);

        if (min <= 0 || max <= 0) {
            setPriceError("Prices must be greater than 0");
            return;
        }

        if (min > max) {
            setPriceError("Min price cannot be greater than Max");
            return;
        }

        if (min > 99999 || max > 99999) {
            setPriceError("Price cannot exceed 99,999");
            return;
        }

        setPriceError("");
        const customRange = `${min}-${max}`;
        // Single select: Replace existing
        onBudgetChange([customRange]);
        setMinPrice("");
        setMaxPrice("");
    };

    return (
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm sticky top-24 space-y-6 p-2">

            {/* Clear Filters Header */}
            {showClear && (
                <>             <div className="flex items-center justify-between">
                    <ButtonTertiary
                        className="!px-3 !py-1 text-sm text-red-500 border border-red-500 hover:bg-red-50"
                        onClick={onClear}
                    >
                        Clear Filters
                    </ButtonTertiary>
                </div>
                    <div className="w-full border-t border-gray-100"></div>
                </>)}

            {/* Explore All Products CTA */}
            <ExploreSidebarCard to="/products" icon={ShoppingBag} text="Explore All Products" />

            <div className="w-full border-t border-gray-100"></div>

            {/* Budget/Price Section */}
            <div className="px-6">
                <div
                    className="flex items-center justify-between cursor-pointer mb-4"
                    onClick={() => toggleSection("budget")}
                >
                    <h3 className="font-bold text-gray-900 text-lg">₹ Price</h3>
                    {expandedSections.budget ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>

                {expandedSections.budget && (
                    <div className="space-y-4">
                        <div className="space-y-3">
                            {filters.budgets.filter(f => f.id !== 'all_budget').map((filter) => (
                                <div key={filter.id}>
                                    <Checkbox
                                        name={filter.label}
                                        label={filter.label}
                                        checked={selectedBudgets.includes(filter.value || "")}
                                        onChange={(checked) => handleBudgetToggle(filter.value, checked)}
                                        sizeClassName="w-5 h-5"
                                        labelClassName="text-sm text-gray-700"
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Custom Range */}
                        <div className="pt-4 border-t border-gray-100">
                            <p className="text-sm font-medium text-gray-700 mb-3">Custom Range:</p>
                            <form onSubmit={handleCustomPriceSubmit} className="space-y-3">
                                <div className="flex gap-2">
                                    <input
                                        type="number"
                                        placeholder="Min ₹"
                                        value={minPrice}
                                        onChange={(e) => handlePriceInputChange(e, setMinPrice)}
                                        className={`w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 transition-colors ${priceError
                                            ? "border-red-500 focus:ring-red-500"
                                            : "border-gray-300 focus:ring-indigo-500"
                                            }`}
                                    />
                                    <input
                                        type="number"
                                        placeholder="Max ₹"
                                        value={maxPrice}
                                        onChange={(e) => handlePriceInputChange(e, setMaxPrice)}
                                        className={`w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 transition-colors ${priceError
                                            ? "border-red-500 focus:ring-red-500"
                                            : "border-gray-300 focus:ring-indigo-500"
                                            }`}
                                    />
                                </div>
                                {priceError && (
                                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                                        <div className="flex items-center">
                                            <span className="mr-1">⚠️</span>
                                            {priceError}
                                        </div>
                                    </div>
                                )}
                                <ButtonSecondary
                                    type="submit"
                                    size="sm"
                                    className="w-full !text-xs !py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800"
                                    disabled={!minPrice || !maxPrice}
                                >
                                    Apply Custom Range
                                </ButtonSecondary>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            {/* Categories Section */}
            <div className="border-b border-gray-100 pb-6">
                <div
                    className="flex items-center justify-between cursor-pointer mb-4"
                    onClick={() => toggleSection("categories")}
                >
                    <h3 className="font-bold text-gray-900 text-lg">Category</h3>
                    {expandedSections.categories ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>

                {expandedSections.categories && (
                    <div className="space-y-3">
                        {filters.types.filter(f => f.id !== 'all').map((filter) => (
                            <div key={filter.id}>
                                <Checkbox
                                    name={filter.label}
                                    label={filter.label}
                                    checked={selectedTypes.includes(filter.value || "")}
                                    onChange={(checked) => handleTypeToggle(filter.value, checked)}
                                    sizeClassName="w-5 h-5"
                                    labelClassName="text-sm text-gray-700"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KitsDesktopFilterSidebar;
